import { useEffect, useState } from "react";
import { api, notifyError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Save, Loader2, ShieldCheck, Mail, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function MasterProfile() {
  const { refresh, user } = useAuth();
  const [email, setEmail] = useState("");
  const [originalEmail, setOriginalEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master/admin/info");
      setEmail(data?.email || "");
      setOriginalEmail(data?.email || "");
    } catch (e) {
      notifyError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const emailChanged = email.trim().toLowerCase() !== (originalEmail || "").toLowerCase();
    const wantsPwd = !!password;

    if (!emailChanged && !wantsPwd) {
      toast.info("Değişiklik yapmadınız");
      return;
    }
    if (wantsPwd) {
      if (password.length < 6) {
        toast.error("Şifre en az 6 karakter olmalıdır");
        return;
      }
      if (password !== passwordConfirm) {
        toast.error("Şifre tekrarı eşleşmiyor");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {};
      if (emailChanged) payload.email = email.trim();
      if (wantsPwd) payload.password = password;
      await api.put("/master/admin/update", payload);
      toast.success("Profil bilgileri güncellendi");
      setPassword("");
      setPasswordConfirm("");
      await load();
      await refresh();
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto space-y-6" data-testid="master-profile-page">
      <div>
        <div className="label-eyebrow text-amber-600">Hesap</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">Profil Bilgileri</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Master admin hesabınıza ait e-posta ve şifrenizi buradan güncelleyebilirsiniz.
        </p>
      </div>

      <Card className="rounded-sm p-5 sm:p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-sm bg-amber-500 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-foreground" />
          </div>
          <div>
            <div className="font-display font-bold text-lg tracking-tight">Master Admin</div>
            <div className="text-xs text-muted-foreground">{user?.name || "Master"}</div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10">
            <Loader2 className="h-6 w-6 animate-spin inline" />
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="master-profile-form">
            <div className="space-y-1.5">
              <Label className="label-eyebrow flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" /> E-posta
              </Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-sm"
                data-testid="master-profile-email"
              />
              <p className="text-xs text-muted-foreground">
                Bir sonraki girişinizde bu e-posta ile oturum açacaksınız.
              </p>
            </div>

            <div className="border-t border-border pt-5 space-y-3">
              <div className="label-eyebrow flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" /> Şifre Değiştir (opsiyonel)
              </div>

              <div className="space-y-1.5">
                <Label className="label-eyebrow">Yeni Şifre</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="En az 6 karakter (boş bırakırsanız değişmez)"
                    className="rounded-sm pr-10"
                    data-testid="master-profile-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="label-eyebrow">Yeni Şifre (Tekrar)</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  placeholder="Aynı şifreyi tekrar yazın"
                  className="rounded-sm"
                  data-testid="master-profile-password-confirm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-border pt-5">
              <Button
                type="submit"
                disabled={saving}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                data-testid="master-profile-submit"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Kaydet
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
